const IS_SERVER = typeof window === "undefined";

const isAbsolute = (value?: string): boolean =>
  !!value && /^https?:\/\//i.test(value.trim());

/**
 * The backend base URL for the CURRENT execution context. The two contexts need
 * genuinely different values, which is why this is not just an env read.
 *
 * Browser -> the `NEXT_PUBLIC_` value verbatim, normally `/proxy/server/api`. Relative
 * is the POINT: it keeps the call same-origin, so the `sameSite: "strict"` auth cookie
 * the backend sets is first-party. See src/app/proxy/server/[...path]/route.ts.
 *
 * Server -> an ABSOLUTE upstream, always. Node's axios and fetch cannot resolve a
 * relative base, so a relative value makes every server component throw and fall back:
 * layout.tsx, sitemap.ts and every (user)/(root) page render with no data at all and
 * the site serves `<title>University</title>`. Nothing surfaces in the browser to
 * explain it, which is what makes this worth a named helper instead of an inline `??`.
 *
 * Server-side preference order: the server-only override from .env.example first, then
 * the public value if it is still absolute.
 */
function contextualBaseUrl(
  serverOnly: string | undefined,
  publicValue: string | undefined,
  serverOnlyName: string
): string | undefined {
  if (!IS_SERVER) return publicValue;

  const absolute = [serverOnly, publicValue].find(isAbsolute);
  if (absolute) return absolute.trim().replace(/\/+$/, "");

  // Only reachable when the public value was pointed at the proxy but the upstream was
  // never configured. Resolving the relative path against this deployment's own origin
  // keeps SSR rendering; it cannot recurse, because the proxy route answers 503 when it
  // has no upstream either.
  const fallback = publicValue?.trim();
  if (fallback?.startsWith("/")) {
    console.error(
      `[config/urls] ${serverOnlyName} is not set and the public base is relative ("${fallback}"). ` +
        `Server-side requests are falling back to this deployment's own origin. Set ${serverOnlyName} to an absolute http(s) URL.`
    );
    return `${getPublicSiteUrl()}${fallback}`;
  }
  return publicValue;
}

export const APP_URL = {
  API_BASE_URL: contextualBaseUrl(
    process.env["API_BASE_URL"],
    process.env["NEXT_PUBLIC_API_BASE_URL"],
    "API_BASE_URL"
  ),
  PROXY_API_BASE_URL: contextualBaseUrl(
    process.env["PROXY_API_BASE_URL"],
    process.env["NEXT_PUBLIC_PROXY_API_BASE_URL"],
    "PROXY_API_BASE_URL"
  ),
  NEXT_PUBLIC_APP_BASE_URL: process.env["NEXT_PUBLIC_APP_BASE_URL"],
  NEXT_PUBLIC_RUDDERSTACK_SOURCE_WRITE_KEY: process.env["NEXT_PUBLIC_RUDDERSTACK_SOURCE_WRITE_KEY"],
  S3_CDN: process.env["NEXT_PUBLIC_S3_CDN"],
  /**
   * upGrad internship service host, called directly from the browser. Same
   * variable name and value as KUK's `NEXT_PUBLIC_INTERNSHIP_PLATFORM_SERVICE`,
   * so the two projects stay configured identically.
   *
   * The host does NOT include /api — each endpoint adds it, as KUK does.
   */
  INTERNSHIP_PLATFORM_SERVICE:
    process.env["NEXT_PUBLIC_INTERNSHIP_PLATFORM_SERVICE"],
  /**
   * Atom Paynetz checkout SDK, injected on demand by `useAtomPayment` when a
   * student pays for an internship.
   *
   * Deliberately NO fallback. A hard-coded default here would be a payment
   * endpoint baked into the bundle: an unset variable would silently send real
   * students to whichever environment the literal happened to name. Unset fails
   * loudly instead — see `useAtomPayment`.
   *
   * The environment the SDK is constructed with is NOT here: the source project
   * passes "uat" as a literal, so it lives as a constant in `useAtomPayment`.
   */
  ATOM_SDK_URL: process.env["NEXT_PUBLIC_ATOM_SDK_URL"],
  /** upGrad Learn, linked from the "Start Learning" button after a paid enrolment. */
  LEARN_URL: process.env["NEXT_PUBLIC_LEARN_URL"],
};

/** Canonical site origin for robots.txt, sitemap, and absolute links (no trailing slash). */
export function getPublicSiteUrl(): string {
  const explicit = process.env["NEXT_PUBLIC_APP_BASE_URL"]?.trim();
  if (explicit) return explicit.replace(/\/+$/, "");
  const vercel = process.env["VERCEL_URL"]?.trim();
  if (vercel) return `https://${vercel.replace(/^https?:\/\//, "")}`;
  return "http://localhost:3000";
}
